import { useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { gql, useLazyQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

import useForm, { FieldList } from "../../hooks/useForm";
import { FLAG_ZITADEL_AZURE_LOGIN } from "../../includes/featureFlags";
import { hasFeatureFlag } from "../../includes/site";
import { useAppState } from "../../contexts/AppContext";
import { AUTH_URL, getUrl } from "../../includes/routes";
import { LoginInfoManager } from "../../includes/loginInfoManager";
import { FAILED_AUTHENTICATION_PARAM, ZITADEL_IDP_AUTH_BACK_URL_PARAM } from "../../includes/constants";
import Button from "../Button";
import Form from "../form/Form";
import LabelledField from "../form/LabelledField";
import Input from "../form/Input";
import FormActions from "../form/FormActions";

type ZitadelGetIdpAuthUrlVariables = {
    schoolUniqueKey: string;
    idpName: string;
    backUrl: string;
}

type ZitadelGetIdpAuthUrlResponse = {
    zitadelGetIdpAuthUrl: string;
}

const validator = (fields: FieldList) => {
    const errors: any = {};

    if (!fields.schoolUniqueKey) {
        errors.schoolUniqueKey = 'Please provide a school code';
    }

    return errors;
};

const IdpAuthInitialisation = () => {
    const [appState] = useAppState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelAzureLoginFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_AZURE_LOGIN);

    const [searchParams] = useSearchParams();
    const backUrl = searchParams.get(ZITADEL_IDP_AUTH_BACK_URL_PARAM) ?? "";

    const { formState: fields, formErrors: errors, onFieldChange, onFieldInvalid, validate } = useForm({
        fields: { schoolUniqueKey: LoginInfoManager.get().schoolUniqueKey ?? "" },
        validator,
    });

    const [zitadelGetIdpAuthUrl] = useLazyQuery<ZitadelGetIdpAuthUrlResponse, ZitadelGetIdpAuthUrlVariables>(
        ZITADEL_GET_IDP_AUTH_URL,
    );

    const onSubmit = useCallback(() => {
        if (validate()) {
            setIsSubmitting(true);
            zitadelGetIdpAuthUrl({
                variables: {
                    schoolUniqueKey: fields.schoolUniqueKey,
                    idpName: 'Azure',
                    backUrl,
                },
            }).then(({ error, data }) => {
                const url = data?.zitadelGetIdpAuthUrl;

                // According to Apollo docs the lazy query function always returns a promise.
                // So unfortunately, we have to manually check for an "error".
                if (error || !url) {
                    throw new Error("Login attempt did not return a valid response.");
                }

                // Avoids opening the new url in a new tab
                window.location.replace(url);
            }).catch(() => {
                window.location.href = getUrl(AUTH_URL) + "?" + FAILED_AUTHENTICATION_PARAM;
            })
        } else {
            // Screen reader announces a field error message.
            const firstTargetKey = Object.keys(errors)?.[0] ?? null;
            if (firstTargetKey) {
                const firstElement = document.getElementsByName(Object.keys(errors)[0]);
                if (firstElement.length > 0) {
                    firstElement[0].focus();
                }
            }
        }
    }, [fields, errors, validate, zitadelGetIdpAuthUrl, backUrl]);

    useEffect(function redirectUser() {
        // Wait for feature flags to completely load first
        if (isFeatureFlagsLoading) return;

        // If feature flag is missing, redirect back to login page
        if (!hasZitadelAzureLoginFeatureFlag) window.location.href = getUrl(AUTH_URL);
    }, [isFeatureFlagsLoading, hasZitadelAzureLoginFeatureFlag]);

    if (isFeatureFlagsLoading) {
        <div>
            loading
        </div>
    }

    return (
        <div css={authContentStyle}>
            <div css={headerStyle}>
                <h1 css={leftHeaderStyle}>Sign in with Azure</h1>
            </div>
            <div css={formContainerStyle}>
                <Form onSubmit={onSubmit}>
                    <LabelledField
                        name="schoolUniqueKey"
                        error={errors.schoolUniqueKey}
                        label="School Code"
                        version={'normal'}
                    >
                        <Input
                            hasError={!!errors.schoolUniqueKey}
                            // css={emailInputStyle}
                            label="Enter school code"
                            name="schoolUniqueKey"
                            onChange={onFieldChange}
                            onInvalid={onFieldInvalid}
                            value={fields.schoolUniqueKey}
                            hasBorder
                            required
                        />
                    </LabelledField>
                    <div css={formActionsStyle}>
                        <FormActions customCss={customFormActionsStyles}>
                            <Button
                                css={formSubmitButtonStyle}
                                type="submit"
                                forceLoading={isSubmitting}
                                useOutline
                            >
                                Log in
                            </Button>
                        </FormActions>
                    </div>
                </Form>
            </div>
        </div>
    );
}

// Copied from SignIn.tsx
const authContentStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// Copied from SignIn.tsx
const headerStyle = css`
    display: flex;
    justify-content: space-between;
`;

// Copied from SignIn.tsx
const leftHeaderStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

// Copied from SignIn.tsx
const formContainerStyle = (theme: EmotionTheme) => css`
    ${theme.breakpoints.up("5xl")} {
        margin-top: 18px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin-top: 21px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin-top: 13px;
    }

    ${theme.breakpoints.only("md")} {
        margin-top: 16px;
    }

    ${theme.breakpoints.only("sm")} {
        margin-top: 8px;
    }

    ${theme.breakpoints.down("xs")} {
        margin-top: 6px;
    }
`;

// Copied from SignIn.tsx
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        margin-top: 20px;
        justify-content: center;
    }

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }
`;

// Copied from SignIn.tsx
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

// Copied from SignIn.tsx
const formSubmitButtonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;

const ZITADEL_GET_IDP_AUTH_URL = gql`
    query zitadelGetIdpAuthUrl($schoolUniqueKey: String!, $idpName: String!, $backUrl: String) {
        zitadelGetIdpAuthUrl(schoolUniqueKey: $schoolUniqueKey, idpName: $idpName, backUrl: $backUrl)
    }
`;

export default IdpAuthInitialisation;